import { useState } from "react";
import { useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Image as ImageIcon, Download, Wand2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { aiImage } from "@/lib/ai";
import { resolveMediaUrl } from "@/lib/api";

const PROVIDERS = [
  { id: "gemini", label: "Nano Banana (Gemini)", tag: "Fast · Great for brand assets" },
  { id: "openai", label: "GPT Image 1 (OpenAI)", tag: "Sharper · Better for photorealism" },
];

const KINDS = [
  { id: "banner",       label: "Banner" },
  { id: "thumbnail",    label: "Thumbnail" },
  { id: "cover",        label: "Campaign cover" },
  { id: "social_post",  label: "Social post" },
  { id: "feature_icon", label: "Feature icon" },
  { id: "freeform",     label: "Freeform" },
];

const ImageGenerator = () => {
  const { user, loading: authLoading, requireAuth } = useAuth();
  const [prompt, setPrompt] = useState("");
  const [provider, setProvider] = useState("gemini");
  const [kind, setKind] = useState("banner");
  const [enhance, setEnhance] = useState(true);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (!authLoading && !user) requireAuth("Sign in to generate campaign images."); }, [user, authLoading, requireAuth]);

  const run = async () => {
    if (!prompt.trim()) { toast.error("Describe what you want first"); return; }
    setLoading(true);
    try {
      const r = await aiImage({ prompt, provider, kind, enhance });
      setHistory((prev) => [{ ...r.data, id: r.id, created_at: r.created_at }, ...prev]);
      toast.success("Image ready");
    } catch (e) { toast.error(e.message || "Generation failed"); } finally { setLoading(false); }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10" data-testid="image-generator-page">
      <div>
        <div className="flex items-center gap-2 text-saffron-700"><ImageIcon className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-[0.2em]">Image Generator</span></div>
        <h1 className="mt-1 font-display text-3xl md:text-4xl font-bold tracking-tight text-navy-800">Campaign visuals in seconds.</h1>
        <p className="mt-2 text-navy-600 max-w-2xl">Banners, thumbnails, social posts and feature icons — powered by Gemini Nano Banana or GPT Image 1.</p>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 rounded-2xl border border-warm-200 bg-white p-6 space-y-5">
          <div>
            <label className="text-xs font-semibold text-navy-700 uppercase tracking-wide">Describe your image</label>
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4} placeholder="A minimal editorial banner for a smart water bottle called AquaSense, navy and saffron palette, soft studio lighting" data-testid="ig-prompt" className="mt-1.5 w-full rounded-2xl border border-warm-200 bg-white px-3 py-2 text-sm focus:outline-none focus:border-saffron-500 focus:ring-4 focus:ring-saffron-500/15 transition" />
          </div>

          <div>
            <label className="text-xs font-semibold text-navy-700 uppercase tracking-wide">Kind</label>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {KINDS.map((k) => (
                <button key={k.id} onClick={() => setKind(k.id)} data-testid={`ig-kind-${k.id}`} className={`rounded-full px-3 h-8 text-xs font-semibold transition-colors ${kind === k.id ? "bg-navy-800 text-white" : "text-navy-600 hover:text-navy-800 hover:bg-warm-100 border border-warm-200"}`}>
                  {k.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-navy-700 uppercase tracking-wide">Model</label>
            <div className="mt-2 grid grid-cols-1 gap-2">
              {PROVIDERS.map((p) => (
                <button key={p.id} onClick={() => setProvider(p.id)} data-testid={`ig-provider-${p.id}`} className={`text-left rounded-2xl border p-3 transition ${provider === p.id ? "border-saffron-500 bg-saffron-500/10" : "border-warm-200 bg-white hover:border-saffron-300"}`}>
                  <div className="font-semibold text-navy-800 text-sm">{p.label}</div>
                  <div className="text-[11px] text-navy-500 mt-0.5">{p.tag}</div>
                </button>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-navy-700 cursor-pointer">
            <input type="checkbox" checked={enhance} onChange={(e) => setEnhance(e.target.checked)} data-testid="ig-enhance" className="rounded accent-saffron-600" />
            <Wand2 className="h-3.5 w-3.5 text-saffron-600" /> Auto-enhance my prompt (Claude rewrites for better results)
          </label>

          <button onClick={run} disabled={loading || !prompt.trim()} data-testid="ig-run" className="btn-saffron w-full inline-flex items-center justify-center gap-2 rounded-2xl h-11 font-semibold disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {loading ? "Generating…" : "Generate image"}
          </button>
        </div>

        <div className="lg:col-span-7 space-y-4">
          {loading && history.length === 0 && (<div className="rounded-2xl border border-warm-200 bg-white p-12 text-center"><Loader2 className="h-8 w-8 mx-auto animate-spin text-saffron-600" /><p className="mt-3 text-navy-500">Painting your idea…</p></div>)}
          {!loading && history.length === 0 && (<div className="rounded-2xl border border-warm-200 bg-cream-100 p-10 text-center text-sm text-navy-600">Your generated images will appear here.</div>)}
          <div className="grid grid-cols-1 gap-4" data-testid="ig-gallery">
            {history.map((img, i) => (
              <div key={img.id || i} data-testid={`ig-result-${i}`} className="rounded-2xl border border-warm-200 bg-white overflow-hidden">
                <img src={resolveMediaUrl(img.url)} alt="Generated" className="w-full h-auto object-cover" />
                <div className="p-4 space-y-2">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-saffron-700 flex items-center gap-2">
                    <span>{img.provider === "gemini" ? "Nano Banana" : "GPT Image 1"}</span><span className="text-navy-400">·</span><span className="text-navy-500">{img.kind}</span>
                  </div>
                  <p className="text-xs text-navy-600 line-clamp-3">{img.prompt}</p>
                  <a href={resolveMediaUrl(img.url)} download className="inline-flex items-center gap-1.5 rounded-full border border-warm-200 h-8 px-3 text-[11px] font-semibold text-navy-700 hover:border-saffron-300 hover:text-saffron-700">
                    <Download className="h-3 w-3" /> Download
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageGenerator;
